import React, { useState, useEffect } from 'react';
import { environment } from '../components/Services/environment';
import { getShopUrl, getPlanStatus } from '../components/Services/CommonServices';

const endpoint = environment.serverUrl;

/**
 * Custom hook to manage app installation and authentication status
 */
export const useAppAuth = () => {
  const storeUrl = getShopUrl();
  const [appInstalled, setAppInstalled] = useState("redirect");
  const [trialDays, setTrialDays] = useState(0);
  const [planDownGrade, setPlanDowngrade] = useState(false);
  const [chargeAlert, setChargeAlert] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);

  useEffect(() => {
    if (!storeUrl) return;
    
    // First call checkAppInstalled which may provide plan info
    checkAppInstalled();
    
    // Then optionally load detailed plan info if not already available
    setTimeout(() => {
      if (!planCache.current.data) {
        loadCurrentPlan();
      }
      setPlanLoading(false);
    }, 100); // Small delay to allow installed endpoint to potentially provide plan info
  }, [storeUrl]);

  const checkAppInstalled = () => {
    fetch(endpoint + '/api/Store/installed?storeUrl=' + storeUrl)
      .then(response => response.json())
      .then(data => {
        // Use plan info from installed endpoint if available
        if (data.currentPlanTier) {
          planCache.current = {
            data: data.currentPlanTier,
            timestamp: Date.now(),
            ttl: 30000
          };
          setCurrentPlan(data.currentPlanTier);
        }
        
        if (data.type === "install") {
          window.open(data.redirectUrl, '_top');
        } else if (data.type === "activate-charge") {
          setTrialDays(data.trialDays);
          setChargeAlert(true);
          setAppInstalled("true");
        } else if (data.type === "empty") {
          setAppInstalled("undefined");
        } else if (data.type === "charge-active") {
          setTrialDays(data?.trialDays);
          setAppInstalled("true");
        } else if (data.type === "downgrade-plan") {
          setTrialDays(data.trialDays);
          setAppInstalled("true");
          setPlanDowngrade(true);
        }
      })
      .catch(error => {
        console.error('Error checking app installation:', error);
      });
  };

  // Add caching to prevent redundant API calls
  const planCache = React.useRef({ data: null, timestamp: null, ttl: 30000 }); // 30 second cache

  const loadCurrentPlan = async (forceRefresh = false) => {
    try {
      const now = Date.now();
      
      // Check cache first (unless force refresh)
      if (!forceRefresh && planCache.current.data && 
          planCache.current.timestamp && 
          (now - planCache.current.timestamp) < planCache.current.ttl) {
        setCurrentPlan(planCache.current.data);
        return planCache.current.data;
      }

      const planStatus = await getPlanStatus(storeUrl);
      if (planStatus.success && planStatus.currentPlanTier) {
        // Cache the result
        planCache.current = {
          data: planStatus.currentPlanTier,
          timestamp: now,
          ttl: 30000
        };
        setCurrentPlan(planStatus.currentPlanTier);
        return planStatus.currentPlanTier;
      }
    } catch (error) {
      console.error('Error loading current plan:', error);
      setPlanLoading(false);
    }
  };

  return {
    appInstalled,
    trialDays,
    planDownGrade,
    chargeAlert,
    currentPlan,
    planLoading,
    setTrialDays,
    setPlanDowngrade,
    setChargeAlert,
    storeUrl,
    refreshPlan: (force = false) => loadCurrentPlan(force)
  };
};
